import React, { useEffect, useMemo, useRef } from 'react';
import { defineAdSlot, displayAdSlot, destroyAdSlot } from './gpt';
import { useTrackingAdContext } from './TrackingAdContext';

export interface TrackingAdProps {
  /**
   * Stable identifier for the wrapped item (the game id). Applied as the `s5`
   * targeting value and used as the fallback for the ad container's div id.
   */
  id: string;

  /**
   * Explicit, page-unique id for the hidden slot's DOM container and GPT slot
   * handle. The same `id` (game id) can be rendered in several lobby sections,
   * so callers must supply a composite value (e.g. `${id}-${section}-${slot}`)
   * to avoid duplicate DOM ids that break GPT slot management. Falls back to
   * `tracking-ad-${id}` when omitted.
   */
  divId?: string;

  /** Item content to render. The hidden 1x1 tracking slot is appended after it. */
  children: React.ReactNode;

  /**
   * Explicit ad unit path override. When omitted it is derived from the
   * provider as `/${networkId}/${basePath}`.
   */
  adUnitPath?: string;

  /**
   * Per-item slot-level targeting, merged over the provider's defaultTargeting.
   * Uses the `s1…sn` key scheme (s1 tenant slug, s2 lobby title, s3 section
   * name, s4 image size); `s5` is set automatically from `id` (the game id).
   */
  targeting?: Record<string, string>;

  /** Optional CSS class applied to the wrapper element. */
  className?: string;

  /**
   * Inline style for the wrapper. Defaults to `{ display: 'contents' }` so the
   * wrapper is layout-transparent and does not disturb the host's flex/grid.
   */
  style?: React.CSSProperties;

  /** Wrapper element type (default: "div"). */
  as?: keyof React.JSX.IntrinsicElements;
}

// 1x1 invisible tracking pixel — never renders a visible advert
const TRACKING_SIZE: [number, number][] = [[1, 1]];

const HIDDEN_SLOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  opacity: 0,
  pointerEvents: 'none',
};

/**
 * Build a deterministic string from a targeting map so the slot effect can
 * depend on the *values* rather than the object identity. Callers routinely
 * pass inline `{...}` targeting objects, which change identity on every render;
 * keying on the sorted entries means the slot is only torn down and redefined
 * when a targeting value actually changes.
 */
const targetingKey = (targeting: Record<string, string>): string =>
  JSON.stringify(
    Object.keys(targeting)
      .sort()
      .map(key => [key, targeting[key]])
  );

/**
 * Wraps a lobby item in an invisible 1x1 Google Ad Manager slot used purely for
 * impression tracking. No visible advert is rendered. Requires a
 * <TrackingAdProvider> ancestor for the GAM network configuration.
 *
 * Self-contained (React + local GPT helpers only) so it is portable to other
 * React systems.
 */
export const TrackingAd: React.FC<TrackingAdProps> = ({
  id,
  divId: providedDivId,
  children,
  adUnitPath,
  targeting,
  className,
  style = { display: 'contents' },
  as: Wrapper = 'div',
}) => {
  const { networkId, basePath, enabled, ready, defaultTargeting } = useTrackingAdContext();

  const divId = useMemo(() => providedDivId ?? `tracking-ad-${id}`, [providedDivId, id]);
  const resolvedPath = useMemo(
    () => adUnitPath || `/${networkId}/${basePath}`,
    [adUnitPath, networkId, basePath]
  );
  const resolvedTargeting = useMemo(
    () => ({ ...defaultTargeting, ...targeting, s5: id }),
    [id, defaultTargeting, targeting]
  );
  const resolvedTargetingKey = useMemo(() => targetingKey(resolvedTargeting), [resolvedTargeting]);

  // Read the latest targeting from a ref inside the effect so the effect can
  // depend on the stable, value-based key instead of the object identity.
  const targetingRef = useRef(resolvedTargeting);
  targetingRef.current = resolvedTargeting;

  const isDefinedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !ready) {
      return;
    }

    if (!isDefinedRef.current) {
      defineAdSlot({
        adUnitPath: resolvedPath,
        sizes: TRACKING_SIZE,
        divId,
        targeting: targetingRef.current,
      });
      isDefinedRef.current = true;
    }

    displayAdSlot(divId);

    return () => {
      if (isDefinedRef.current) {
        destroyAdSlot(divId);
        isDefinedRef.current = false;
      }
    };
  }, [enabled, ready, divId, resolvedPath, resolvedTargetingKey]);

  return (
    <Wrapper className={className} style={style}>
      {children}
      {enabled ? <div id={divId} style={HIDDEN_SLOT_STYLE} aria-hidden /> : null}
    </Wrapper>
  );
};

export default TrackingAd;
